import { useEffect, useMemo, useState } from 'react';
import { jsPDF } from 'jspdf';
import api from '../../services/api';import "../../assets/styles/Employee/EmpOffboardingPage.css";

const DEFAULT_PROFILE = { name: '', designation: '', pmName: '', department: '' };

function buildPreview(profile, reason, relievingDate) {
  return `OFFBOARDING REQUEST FORM\n\nEmployee Name: ${profile.name || '-'}\nDesignation: ${profile.designation || '-'}\nProject Manager: ${profile.pmName || '-'}\nDepartment: ${profile.department || '-'}\n\nReason: ${reason || '-'}\nRequested Relieving Date: ${relievingDate || '-'}\n\nDeclaration:\nI request HR and PM to process my offboarding formalities.`;
}

function formatAmount(v) {
  return `INR ${Number(v || 0).toLocaleString('en-IN')}`;
}

function EmpOffboardingPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState('');
  const [profile, setProfile] = useState(DEFAULT_PROFILE);
  const [reason, setReason] = useState('');
  const [relievingDate, setRelievingDate] = useState('');

  const [assetReqId, setAssetReqId] = useState('');
  const [assetRows, setAssetRows] = useState([]);
  const [assetChoice, setAssetChoice] = useState({});
  const [assetNote, setAssetNote] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [reqRows, p, me] = await Promise.all([
      api.getEmployeeLifecycleRequests('offboarding').catch(() => []),
      api.getProfile().catch(() => ({})),
      api.getMe().catch(() => ({}))]
      );
      const requestRows = Array.isArray(reqRows) ? reqRows : [];
      setRows(requestRows);
      const pr = p?.profile || p || {};
      const latestPayload = requestRows.find((r) => r?.employee_payload)?.employee_payload || null;
      setProfile({
        name: latestPayload?.employeeName || pr.full_name || pr.name || me?.name || '',
        designation: latestPayload?.designation || pr.designation || me?.designation || '',
        pmName: latestPayload?.pmName || requestRows[0]?.pm_name || pr.manager || pr.manager_name || me?.manager || me?.manager_name || '',
        department: latestPayload?.department || pr.department || pr.department_name || pr.team || me?.department || me?.team || ''
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {load();}, []);

  useEffect(() => {
    api.getAssets().then((d) => {
      const items = [];
      (d?.assets || []).forEach((a) => items.push({ key: `asset_${a.asset_id || a.id}`, label: `${a.asset_type || 'Asset'} - ${a.brand || ''}`.trim() }));
      const sw = Array.isArray(d?.software) ? d.software : [];
      const ai = Array.isArray(d?.aiTools) ? d.aiTools : [];
      sw.forEach((s, i) => items.push({ key: `sw_${i}`, label: `Software: ${s}` }));
      ai.forEach((s, i) => items.push({ key: `ai_${i}`, label: `AI Tool: ${s}` }));
      setAssetRows(items);
    }).catch(() => setAssetRows([]));
  }, []);

  const activeRequest = useMemo(() => rows[0] || null, [rows]);
  const statusLower = String(activeRequest?.status || '').toLowerCase();
  const initReq = useMemo(() => rows.find((r) => String(r.status || '').toLowerCase() === 'awaiting_employee_form') || null, [rows]);
  const formReq = initReq || activeRequest;
  const noticeInfo = useMemo(() => {
    if (!activeRequest?.notice_start_date || !activeRequest?.notice_end_date) return null;
    return { start: activeRequest.notice_start_date, end: activeRequest.notice_end_date, duration: activeRequest.notice_duration || '-' };
  }, [activeRequest]);
  const canFormSubmit = !!formReq && ['awaiting_employee_form', 'resend_employee'].includes(String(formReq.status || '').toLowerCase());

  const eligibleAssetRows = useMemo(
    () => rows.filter((r) => ['notice_period', 'approved_pm', 'pm_scheduled', 'pending_it', 'it_replied', 'hr_docs_uploaded', 'settlement_pending_pm', 'settlement_rework_hr', 'settlement_approved_pm', 'settlement_sent'].includes(String(r.status || '').toLowerCase())),
    [rows]
  );
  const selectedAssetRequest = useMemo(() => rows.find((r) => String(r.req_id) === String(assetReqId)) || null, [rows, assetReqId]);
  const canAssets = useMemo(() => {
    const target = selectedAssetRequest || activeRequest;
    return !!target && ['notice_period', 'approved_pm', 'pm_scheduled', 'pending_it', 'it_replied', 'hr_docs_uploaded', 'settlement_pending_pm', 'settlement_rework_hr', 'settlement_approved_pm', 'settlement_sent'].includes(String(target.status || '').toLowerCase());
  }, [selectedAssetRequest, activeRequest]);
  const alreadySentToIT = useMemo(() => {
    const target = selectedAssetRequest || activeRequest;
    return ['pending_it', 'it_replied', 'hr_docs_uploaded', 'settlement_pending_pm', 'settlement_rework_hr', 'settlement_approved_pm', 'settlement_sent'].
    includes(String(target?.status || '').toLowerCase());
  }, [selectedAssetRequest, activeRequest]);

  useEffect(() => {
    if (!assetReqId && eligibleAssetRows.length) setAssetReqId(eligibleAssetRows[0].req_id);
  }, [assetReqId, eligibleAssetRows]);

  const settlement = useMemo(() => activeRequest?.hr_payload?.finalSettlement || null, [activeRequest]);
  const finalDocs = useMemo(() => Array.isArray(activeRequest?.hr_payload?.finalDocs) ? activeRequest.hr_payload.finalDocs : [], [activeRequest]);
  const settlementSentAt = useMemo(
    () => activeRequest?.hr_payload?.finalSettlementEmployeeSentAt || activeRequest?.final_settlement_sent_at || '',
    [activeRequest]
  );
  const settlementWindow = useMemo(() => {
    if (!settlementSentAt) return null;
    const sent = new Date(settlementSentAt);
    if (Number.isNaN(sent.getTime())) return null;
    const end = new Date(sent.getTime() + 3 * 24 * 60 * 60 * 1000);
    const now = new Date();
    const daysLeft = Math.max(0, Math.ceil((end - now) / (24 * 60 * 60 * 1000)));
    return { sent, end, active: now <= end, daysLeft };
  }, [settlementSentAt]);
  const hasSettlementStep = !!settlementSentAt;
  const hasFinalDocs = finalDocs.length > 0;
  const sentToItDone = ['pending_it', 'it_replied', 'hr_docs_uploaded', 'settlement_pending_pm', 'settlement_rework_hr', 'settlement_approved_pm', 'settlement_sent'].includes(statusLower);

  const currentStep = !rows.length ?
  1 :
  canFormSubmit ?
  2 :
  !sentToItDone ?
  3 :
  !hasSettlementStep ?
  3 :
  !hasFinalDocs ?
  4 :
  5;

  const showStep2 = currentStep >= 2;
  const showStep3 = currentStep >= 3;
  const showStep4 = currentStep >= 4;
  const showStep5 = currentStep >= 5;

  const downloadPreview = () => {
    const doc = new jsPDF();
    const lines = buildPreview(profile, reason, relievingDate).split('\n');
    let y = 16;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('OFFBOARDING REQUEST FORM', 14, y);
    y += 10;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    lines.forEach((line) => {
      if (y > 282) {doc.addPage();y = 16;}
      doc.text(line || ' ', 14, y);
      y += 6;
    });
    doc.save(`offboarding-request-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const submitForm = async () => {
    if (!formReq?.req_id || !reason || !relievingDate) return;
    try {
      setSubmitting(true);
      await api.submitEmployeeOffboardingForm({ reqId: formReq.req_id, reason, relievingDate });
      setToast('Form sent to Manager and HR');
      setTimeout(() => setToast(''), 2500);
      await load();
    } catch (e) {
      setToast(e.message || 'Failed');
      setTimeout(() => setToast(''), 2500);
    } finally {
      setSubmitting(false);
    }
  };

  const submitAssetsToIT = async () => {
    if (!assetReqId) return;
    if (!canAssets) {
      setToast('This request is not yet eligible for IT submission.');
      setTimeout(() => setToast(''), 2500);
      return;
    }
    const available = assetRows.filter((a) => assetChoice[a.key] === 'yes').map((a) => a.label);
    const missing = assetRows.filter((a) => assetChoice[a.key] === 'no').map((a) => a.label);
    if (assetRows.length && available.length + missing.length === 0) {
      setToast('Select Yes/No for checklist items before sending.');
      setTimeout(() => setToast(''), 2500);
      return;
    }
    try {
      setSubmitting(true);
      await api.submitEmployeeOffboardingAssets({ reqId: assetReqId, availableAssets: available, missingAssets: missing, note: assetNote || '' });
      setToast('Checklist sent to IT Admin and HR');
      setTimeout(() => setToast(''), 2500);
      await load();
    } catch (e) {
      setToast(e.message || 'Failed');
      setTimeout(() => setToast(''), 2500);
    } finally {
      setSubmitting(false);
    }
  };

  const downloadSettlementPdf = () => {
    if (!settlement) return;
    const basicDue = Number(settlement.basicDue || 0);
    const leaveEncash = Number(settlement.leaveEncash || 0);
    const bonus = Number(settlement.bonus || 0);
    const gratuity = Number(settlement.gratuity || 0);
    const advanceDeduct = Number(settlement.advanceDeduct || 0);
    const assetDeduct = Number(settlement.assetDeduct || 0);
    const noticePay = Number(settlement.noticePay || 0);
    const otherDeduct = Number(settlement.otherDeduct || 0);
    const total = basicDue + leaveEncash + bonus + gratuity - (advanceDeduct + assetDeduct + noticePay + otherDeduct);

    const doc = new jsPDF();
    let y = 16;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('FINAL SETTLEMENT', 14, y);
    y += 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Employee: ${profile.name || '-'}`, 14, y);y += 6;
    doc.text(`Designation: ${profile.designation || '-'}`, 14, y);y += 6;
    doc.text(`Department: ${profile.department || '-'}`, 14, y);y += 8;

    doc.setFont('helvetica', 'bold');
    doc.text('EARNINGS', 14, y);y += 6;
    doc.setFont('helvetica', 'normal');
    [['Basic Salary', basicDue], ['Leave Encashment', leaveEncash], ['Bonus', bonus], ['Gratuity', gratuity]].forEach(([k, v]) => {
      doc.text(String(k), 14, y);
      doc.text(formatAmount(v), 150, y, { align: 'right' });
      y += 6;
    });
    y += 2;
    doc.setFont('helvetica', 'bold');
    doc.text('DEDUCTIONS', 14, y);y += 6;
    doc.setFont('helvetica', 'normal');
    [['Advance', advanceDeduct], ['Asset Deduction', assetDeduct], ['Notice Pay', noticePay], ['Other Deduction', otherDeduct]].forEach(([k, v]) => {
      doc.text(String(k), 14, y);
      doc.text(formatAmount(v), 150, y, { align: 'right' });
      y += 6;
    });
    y += 2;
    doc.setFont('helvetica', 'bold');
    doc.text('NET AMOUNT', 14, y);
    doc.text(formatAmount(total), 150, y, { align: 'right' });
    doc.save(`final-settlement-${activeRequest?.req_id || 'offboarding'}.pdf`);
  };

  const docByLabel = (labelPart) => finalDocs.find((d) => String(d.label || '').toLowerCase().includes(labelPart));
  const relievingDoc = docByLabel('relieving');
  const experienceDoc = docByLabel('experience');
  const fnfDoc = docByLabel('f&f');

  return (
    <div>
      {toast && <div className="toast">{toast}</div>}

      <div className="cd empoffboardingpage-inline-1">
        <div className="cd-b empoffboardingpage-inline-2">
          {rows.length === 0 ? 'You are an active employee.' : `Current Offboarding Status: ${activeRequest?.status || '-'}`}
        </div>
      </div>

      {noticeInfo &&
      <div className="cd empoffboardingpage-inline-3">
          <div className="cd-b empoffboardingpage-inline-4">
            You are under notice period: {noticeInfo.start} to {noticeInfo.end} ({noticeInfo.duration} days)
          </div>
        </div>
      }

      <div className="cd empoffboardingpage-inline-5">
        <div className="cd-h"><div className="cd-t">Offboarding Progress</div><div className="cd-s">Step by step</div></div>
        <div className="cd-b">
          <div className="empoffboardingpage-inline-6">
            {[1, 2, 3, 4, 5].map((n, idx) => {
              const done = n < currentStep;
              const active = n === currentStep;
              return (
                <div key={n} className="empoffboardingpage-inline-7">
                  {idx < 4 &&
                  <div style={{ "--empoffboardingpage-1-background": n < currentStep ? '#6366f1' : '#d1d5db' }} className="empoffboardingpage-dyn-1" />
                  }
                  <div style={{ "--empoffboardingpage-2-background": done ? '#3b82f6' : active ? '#6366f1' : '#cbd5e1' }} className="empoffboardingpage-dyn-2">
                    {done ? 'OK' : n}
                  </div>
                  <div style={{ "--empoffboardingpage-3-color": active || done ? '#111827' : '#94a3b8' }} className="empoffboardingpage-dyn-3">
                    {n === 1 ? 'Ticket' : n === 2 ? 'Form' : n === 3 ? 'Assets' : n === 4 ? 'Settlement' : 'Exit Docs'}
                  </div>
                </div>);

            })}
          </div>
        </div>
      </div>

      <div className="cd empoffboardingpage-inline-8">
        <div className="cd-h"><div className="cd-t">Step 1 - Raise Ticket</div><div className="cd-s">Help Desk -> Raise ticket to HR</div></div>
        <div className="cd-b empoffboardingpage-inline-9">
          Complete existing HR helpdesk ticket flow first. HR will then click proceed from HR Offboarding.
        </div>
      </div>

      {showStep2 &&
      <div className="cd empoffboardingpage-dyn-4" style={{ "--empoffboardingpage-4-opacity": canFormSubmit ? 1 : 0.75 }}>
          <div className="cd-h"><div className="cd-t">Step 2 - Offboarding Form</div><div className="cd-s">Reason + Relieving date</div></div>
          <div className="cd-b">
            {!formReq ? <div className="empoffboardingpage-inline-10">Waiting for HR to initiate offboarding process.</div> :
          <>
                <div className="fg2">
                  <input className="fi" value={profile.name} readOnly placeholder="Name" />
                  <input className="fi" value={profile.designation} onChange={(e) => setProfile((p) => ({ ...p, designation: e.target.value }))} placeholder="Designation" />
                  <input className="fi" value={profile.pmName} onChange={(e) => setProfile((p) => ({ ...p, pmName: e.target.value }))} placeholder="Project Manager" />
                  <input className="fi" value={profile.department} readOnly placeholder="Department" />
                </div>
                <textarea className="fta" rows={3} placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
                <input className="fi" type="date" value={relievingDate} onChange={(e) => setRelievingDate(e.target.value)} />
                <div className="empoffboardingpage-inline-11">
                  {buildPreview(profile, reason, relievingDate)}
                </div>
                <div className="empoffboardingpage-inline-12">
                  <button className="btn-o" onClick={downloadPreview}>Download Form</button>
                  <button className="btn" disabled={submitting || !canFormSubmit || !reason || !relievingDate} onClick={submitForm}>{submitting ? 'Sending...' : 'Send To Manager and HR'}</button>
                </div>
              </>
          }
          </div>
        </div>
      }

      {showStep3 &&
      <div className="cd empoffboardingpage-dyn-5" style={{ "--empoffboardingpage-5-opacity": canAssets ? 1 : 0.75 }}>
          <div className="cd-h"><div className="cd-t">Step 3 - Asset and Credential Checklist</div><div className="cd-s">Send to IT Admin</div></div>
          <div className="cd-b">
            <select className="fi" value={assetReqId} onChange={(e) => setAssetReqId(e.target.value)}>
              <option value="">Select Request</option>
              {eligibleAssetRows.map((r) => <option key={r.req_id} value={r.req_id}>{r.req_id} ({r.status})</option>)}
            </select>
            {assetRows.length === 0 ? <div className="empoffboardingpage-inline-13">No assigned assets/credentials found</div> :
          <div className="empoffboardingpage-inline-14">
                {assetRows.map((a) =>
            <div key={a.key} className="empoffboardingpage-inline-15">
                    <div className="empoffboardingpage-inline-16">{a.label}</div>
                    <label className="empoffboardingpage-inline-17"><input type="radio" name={a.key} checked={assetChoice[a.key] === 'yes'} onChange={() => setAssetChoice((p) => ({ ...p, [a.key]: 'yes' }))} /> Yes</label>
                    <label className="empoffboardingpage-inline-18"><input type="radio" name={a.key} checked={assetChoice[a.key] === 'no'} onChange={() => setAssetChoice((p) => ({ ...p, [a.key]: 'no' }))} /> No</label>
                  </div>
            )}
              </div>
          }
            <div className="empoffboardingpage-inline-19">
              Not Returned: {assetRows.filter((a) => assetChoice[a.key] === 'no').map((a) => a.label).join(', ') || '-'}
            </div>
            <textarea className="fta" rows={2} placeholder="Checklist note" value={assetNote} onChange={(e) => setAssetNote(e.target.value)} />
            <div className="empoffboardingpage-inline-20">
              <button className="btn" disabled={submitting || !assetReqId || !canAssets || alreadySentToIT} onClick={submitAssetsToIT}>
                {submitting ? 'Sending...' : alreadySentToIT ? 'Sent To IT Admin' : 'Send To IT Admin'}
              </button>
            </div>
          </div>
        </div>
      }

      {showStep4 &&
      <div className="cd empoffboardingpage-inline-21">
          <div className="cd-h"><div className="cd-t">Step 4 - Exit Settlement</div><div className="cd-s">Download within 3 days from HR send</div></div>
          <div className="cd-b">
            {!settlement ?
          <div className="empoffboardingpage-inline-22">Settlement is not available yet.</div> :

          <>
                <div className="empoffboardingpage-inline-23">
                  <div>Basic Due: <b>{formatAmount(settlement.basicDue)}</b></div>
                  <div>Leave Encashment: <b>{formatAmount(settlement.leaveEncash)}</b></div>
                  <div>Bonus: <b>{formatAmount(settlement.bonus)}</b></div>
                  <div>Gratuity: <b>{formatAmount(settlement.gratuity)}</b></div>
                  <div>Advance Deduction: <b>{formatAmount(settlement.advanceDeduct)}</b></div>
                  <div>Asset Deduction: <b>{formatAmount(settlement.assetDeduct)}</b></div>
                  <div>Notice Pay: <b>{formatAmount(settlement.noticePay)}</b></div>
                  <div>Other Deduction: <b>{formatAmount(settlement.otherDeduct)}</b></div>
                </div>
                {settlementWindow ?
            <div style={{ "--empoffboardingpage-6-color": settlementWindow.active ? '#15803d' : '#b45309' }} className="empoffboardingpage-dyn-6">
                    {settlementWindow.active ?
              `Download window active. Days left: ${settlementWindow.daysLeft}` :
              'Download window expired (3 days completed).'}
                  </div> :

            <div className="empoffboardingpage-inline-24">Settlement send date not available.</div>
            }
                <button className="btn" onClick={downloadSettlementPdf} disabled={!settlementWindow || !settlementWindow.active}>Download Settlement PDF</button>
              </>
          }
          </div>
        </div>
      }

      {showStep5 &&
      <div className="cd empoffboardingpage-inline-25">
          <div className="cd-h"><div className="cd-t">Step 5 - Exit Documents</div><div className="cd-s">Relieving letter and experience certificate</div></div>
          <div className="cd-b">
            {[
          { label: 'Relieving Letter', doc: relievingDoc },
          { label: 'Experience Certificate', doc: experienceDoc },
          { label: 'F&F Statement', doc: fnfDoc }].
          map((item) =>
          <div key={item.label} className="empoffboardingpage-inline-26">
                <div>{item.label}</div>
                {item.doc?.file_url ?
            <button className="btn-o btn-s" onClick={() => api.downloadFile(item.doc.file_url, item.doc.file_name || item.label)}>Download</button> :
            <span className="empoffboardingpage-inline-27">Pending upload</span>}
              </div>
          )}
          </div>
        </div>
      }

      <div className="cd">
        <div className="cd-h"><div className="cd-t">Offboarding Status Timeline</div><div className="cd-s">{rows.length} requests</div></div>
        <div className="cd-b empoffboardingpage-inline-28">
          {loading ? <div className="empoffboardingpage-inline-29">Loading...</div> :
          <table className="tbl">
              <thead><tr><th>Req ID</th><th>Status</th><th>Notice</th><th>PM Comment</th></tr></thead>
              <tbody>
                {rows.length === 0 ? <tr><td colSpan="4" className="empoffboardingpage-inline-30">No offboarding requests</td></tr> : rows.map((r) =>
              <tr key={r.req_id}>
                    <td className="empoffboardingpage-inline-31">{r.req_id}</td>
                    <td><span className="chip chip-wn">{r.status}</span></td>
                    <td>{r.notice_start_date ? `${r.notice_start_date} to ${r.notice_end_date || '-'} (${r.notice_duration || '-'} days)` : '-'}</td>
                    <td>{r.pm_comment || '-'}</td>
                  </tr>
              )}
              </tbody>
            </table>
          }
        </div>
      </div>
    </div>);

}

export default EmpOffboardingPage;